import React, { useEffect, useState } from "react";
import { FaUserCog } from "react-icons/fa";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "../../master/academicConfiguration/AcademicCreate.module.css";
import {
  CustomButton,
  CustomTextInput,
  CustomDropdown,
} from "../../../../components";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import PopupModal from "../../../../components/PopupModal/PopupModal";

const CreateTimeTable = () => {
  const navigate = useNavigate();
  const { translations } = useSelector((state) => state.language);
  const t = translations?.labels;

  const location = useLocation();
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const client_id = clientData?.client_id || "";
  const school_id = clientData?.school_id || "";
  const user_id = user?.id || "";
  const academic_year = clientData?.academic_year || "";

  /* ===========================
     EDIT / CREATE MODE
  =========================== */
  const editData = location.state?.data;
  const { DAY, CLASS, SECTION, SHIFT, VIEW } = location.state || {};
  const isEdit = Boolean(VIEW);
  // Class/Section/Shift are always chosen on the previous list page before
  // reaching this screen — lock them here instead of re-deriving/re-matching.
  const lockClassSectionShift = Boolean(CLASS);

  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  /* ===========================
     HEADER STATE
  =========================== */
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  const [classValue, setClassValue] = useState("");
  const [sectionValue, setSectionValue] = useState("");
  const [dayValue, setDayValue] = useState("");
  const [shiftValue, setShiftValue] = useState("");

  // ✅ NEW: store shift_id from VIEW API response
  const [viewShiftId, setViewShiftId] = useState(null);

  const [data, setData] = useState([]);
  const [sectionList, setSectionList] = useState([]);
  const [shiftList, setShiftList] = useState([]);
  const [classSubjectList, setClassSubjectList] = useState([]);
  const [viewData, setViewData] = useState();
  const [status, setStatus] = useState(false);

  /* ===========================
     PERIOD STATE
  =========================== */
  const [periods, setPeriods] = useState(
    Array.from({ length: 8 }, (_, i) => ({
      period: i + 1,
      subject: "",
    })),
  );

  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    message: "",
    redirectPath: "",
  });

  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({ ...prev, open: false }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };

  /* ===========================
     PREFILL FROM LOCATION STATE
  =========================== */
  useEffect(() => {
    if (CLASS) setClassValue(CLASS);
    if (SECTION) setSectionValue(SECTION);
    if (SHIFT) setShiftValue(SHIFT);
  }, [CLASS, SECTION, SHIFT]);

  /* ===========================
     PREFILL FROM VIEW API DATA
     Class/Section/Shift already arrive resolved via location.state
     (CLASS/SECTION/SHIFT) — only day + shift id bookkeeping needed here.
  =========================== */
  useEffect(() => {
    if (!viewData || !viewData.length) return;
    const firstRow = viewData[0];

    setDayValue(firstRow.day);

    // ✅ Capture shift_id from VIEW API response
    if (firstRow.shift_id) {
      setViewShiftId(firstRow.shift_id);
    }
  }, [viewData]);

  useEffect(() => {
    if (!Array.isArray(viewData) || viewData.length === 0) return;
    setPeriods((prev) =>
      prev.map((periodItem) => {
        const matched = viewData.find((v) => v.period === periodItem.period);
        if (!matched) return periodItem;
        return {
          ...periodItem,
          id: matched.id,
          subject: matched.subject_id,
        };
      }),
    );
  }, [viewData]);

  /* ===========================
     FETCH ON MOUNT
  =========================== */
  useEffect(() => {
    if (school_id) {
      fetchClassList();
      fetchShiftList();
    }
  }, [school_id]);

  useEffect(() => {
    if (classValue && sectionValue && shiftValue) {
      fetchClassSubjectSList();
    }
  }, [classValue, sectionValue, shiftValue]);

  useEffect(() => {
    if (classValue) fetchSectionList();
  }, [classValue]);

  useEffect(() => {
    if (isEdit) fetchScheduleView();
  }, [status, isEdit]);

  /* ===========================
     API CALLS
  =========================== */
  const fetchScheduleView = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.post(`/timeTable/scheduleView`, {
        school_id: school_id,
        class_id: CLASS?.value,
        section: SECTION?.value,
        shift_id: SHIFT?.value,
        day: DAY,
      });

      if (response?.data?.status) {
        const apiData = response.data.data || [];
        setStatus(true);
        setViewData(apiData);

        // ✅ Also capture shift_id directly here as a fallback
        if (apiData.length > 0 && apiData[0].shift_id) {
          setViewShiftId(apiData[0].shift_id);
        }
      } else {
        setViewData([]);
      }
    } catch (error) {
      console.error("Error fetching schedule view:", error);
      setViewData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSectionList = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.post(
        `/school/timeTable/sectionDropdown `,
        { id: [classValue.class_id], school_id: school_id },
      );
      if (response?.data?.status) {
        const apiData = response.data.data || [];
        setSectionList(
          apiData.map((item) => ({
            id: item.id,
            class_id: item.class_id,
            class: item.section_id,
            label: item.section_id,
            value: item.class_id,
          })),
        );
      } else {
        setSectionList([]);
      }
    } catch (error) {
      console.error("Error fetching section list:", error);
      setSectionList([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchClassList = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.post(`/school/classList`, {
        id: school_id,
        academic_year: academic_year,
      });
      if (response?.data?.success) {
        const apiData = response.data.data || [];
        setData(
          apiData
            .filter((item) => item.domain_id !== "CLASS")
            .map((item) => ({
              id: item.id,
              class_id: item.class_id,
              class: item.domain_id,
              label: item.domain_id,
              value: item.class_id,
              maleCount: item.no_of_boys || 0,
              femaleCount: item.no_of_girls || 0,
              totalStudents: (item.no_of_boys || 0) + (item.no_of_girls || 0),
              noOfSection: item.no_of_section || 0,
            })),
        );
      } else {
        setData([]);
      }
    } catch (error) {
      console.error("Error fetching class list:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchShiftList = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.get(
        `/school/shiftList/${school_id}`,
      );
      if (response?.data?.status) {
        const apiData = response.data.data || [];
        setShiftList(
          apiData.map((item) => ({ value: item.id, label: item.shift_type })),
        );
      } else {
        setShiftList([]);
      }
    } catch (error) {
      console.error("Error fetching shift list:", error);
      setShiftList([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchClassSubjectSList = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.post(`/school/class/subjects`, {
        class_id: classValue.value,
        section: sectionValue.value,
        shift_id: shiftValue.value,
        school_id: school_id,
      });
      if (response?.data?.success) {
        const apiData = response.data.data || [];
        setClassSubjectList(
          apiData.map((item) => ({
            id: item.id,
            class_id: item.class_id,
            class: item.section_id,
            label: item.subject_name,
            value: item.subject_id,
          })),
        );
      } else {
        setClassSubjectList([]);
      }
    } catch (error) {
      console.error("Error fetching subject list:", error);
      setClassSubjectList([]);
    } finally {
      setLoading(false);
    }
  };

  /* ===========================
     PREFILL IN EDIT MODE
  =========================== */
  useEffect(() => {
    if (isEdit && editData) {
      setClassValue(editData.class);
      setSectionValue(editData.section);
      setDayValue(editData.day);
      setShiftValue(editData.shift);
      setPeriods(editData.periods);
    }
  }, [isEdit, editData]);

  /* ===========================
     VALIDATION
  =========================== */
  const validate = () => {
    const newErrors = {};
    if (!classValue) newErrors.class = `${t.classLabel} is required`;
    if (!sectionValue) newErrors.section = `${t.sectionLabel} is required`;
    if (!shiftValue) newErrors.shift = `${t.shiftLabel} is required`;
    periods.forEach((p, i) => {
      if (!p.subject) {
        newErrors[`periodSubject_${i}`] =
          `Period ${p.period} Subject is required`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const clearError = (key) => {
    if (errors[key]) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
    }
  };

  /* ===========================
     HANDLERS
  =========================== */
  const handleSubjectChange = (index, value) => {
    // setPeriods((prev) => {
    //   const updated = [...prev];
    //   updated[index].subject = value?.value || "";
    //   return updated;
    // });

    setPeriods((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        subject:
          value?.value !== undefined && value?.value !== null
            ? Number(value.value)
            : "",
      };
      return updated;
    });
    clearError(`periodSubject_${index}`);
  };

  const handleSubmit = async () => {
    setIsSubmitted(true);
    if (!validate()) return;

    const basePayload = {
      client_id: client_id,
      school_id: school_id,
      class_id: classValue.value,
      section_id: sectionValue.value,
      shift_id: shiftValue.value,
      day: DAY,
      created_by: user_id,
    };

    const subjectsPayload = isEdit
      ? periods.map((p) => ({
          id: p.id,
          period: p.period,
          subject: Number(p.subject),
        }))
      : periods.map((p) => ({ period: p.period, subject: Number(p.subject) }));

    try {
      const response = isEdit
        ? await AxiosInstance.put("/timeTable/scheduleUpdate", {
            ...basePayload,
            subjects: subjectsPayload,
          })
        : await AxiosInstance.post("/timeTable/scheduleCreate", {
            ...basePayload,
            subjects: subjectsPayload,
          });

      if (response?.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message: isEdit
            ? response.data?.message || "Day Schedule Updated Successfully"
            : response.data?.message || "Day Schedule Created Successfully",
          redirectPath: "/user/timeList",
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message: response.data?.message || "Something went wrong",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  /* ===========================
     ✅ DELETE HANDLER
     Uses shift_id from VIEW API response (viewShiftId)
     Falls back to SHIFT from location state
  =========================== */
  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      const payload = {
        client_id: client_id,
        school_id: school_id,
        class_id: CLASS?.value || classValue?.value,
        section_id: SECTION?.value || sectionValue?.value,
        shift_id: viewShiftId || SHIFT?.value || shiftValue?.value, // ✅ Priority: viewShiftId first
        day: DAY || dayValue,
      };

      const response = await AxiosInstance.delete("/timeTable/scheduleDelete", {
        data: payload,
      });

      if (response?.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message: response?.data?.message || t.deleteSuccess,
          redirectPath: "",
        });
        setTimeout(() => {
          navigate(-1);
        }, 1000);
      } else {
        setModalState({
          open: true,
          type: "error",
          message: response?.data?.message || t.deleteFailed,
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Delete API Error:", error);
      setModalState({
        open: true,
        type: "error",
        message: t.somethingWentWrong,
        redirectPath: "",
      });
    } finally {
      setDeleteLoading(false);
      setDeleteModal(false);
    }
  };

  /* ===========================
     RENDER
  =========================== */
  return (
    <>
      {/* Success / Error Modal */}
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? t.success : t.failed}
        message={modalState.message}
        onOk={handleSuccessOk}
      />

      {/* ✅ Delete Confirmation Modal */}
      <PopupModal
        open={deleteModal}
        title={t.delete}
        message={t.confirmDeleteSchedule}
        onOk={handleDelete}
        onClose={() => setDeleteModal(false)}
      />

      <div className={styles.container}>
        {/* HEADER */}
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaUserCog />
          </div>
          <h1 className={styles.headerTitle}>
            {isEdit ? t.editDayScheduleTitle : t.viewDayScheduleTitle}
          </h1>
        </div>

        {/* TOP DETAILS */}
        <div className="row mb-4">
          <div className="col-lg-3 col-md-6 col-sm-12 px-2">
            <CustomDropdown
              label={t.classLabel}
              value={classValue}
              options={data}
              onChange={(v) => {
                setClassValue(v);
                setSectionValue("");
                clearError("class");
              }}
              placeholder={t.classPlaceholder}
              error={errors.class}
              disabled={lockClassSectionShift}
            />
          </div>

          <div className="col-lg-3 col-md-6 col-sm-12 px-2">
            <CustomDropdown
              label={t.sectionLabel}
              value={sectionValue}
              options={sectionList}
              onChange={(v) => {
                setSectionValue(v);
                setClassSubjectList([]);
                setPeriods((prev) =>
                  prev.map((p) => ({
                    ...p,
                    subject: null,
                    ...(isEdit ? {} : { id: undefined }),
                  })),
                );
                clearError("section");
              }}
              placeholder={t.sectionPlaceholder}
              error={errors.section}
              disabled={lockClassSectionShift}
            />
          </div>

          <div className="col-lg-3 col-md-6 col-sm-12 px-2">
            <CustomDropdown
              label={t.shiftLabel}
              value={shiftValue}
              options={shiftList}
              onChange={(v) => {
                setShiftValue(v);
                clearError("shift");
              }}
              placeholder={t.shiftPlaceholder}
              error={errors.shift}
              disabled={lockClassSectionShift}
            />
          </div>

          <div className="col-lg-3 col-md-6 col-sm-12 px-2">
            <CustomTextInput
              label={t.day}
              value={DAY || dayValue}
              onChange={(v) => {
                setDayValue(v);
                clearError("day");
              }}
              disabled
              required
              error={errors.day}
            />
          </div>
        </div>

        {/* PERIODS GRID */}
        <div className="row">
          {periods.map((item, index) => (
            <React.Fragment key={index}>
              <div className="col-lg-2 col-md-3 col-sm-12 px-2 mb-3">
                <CustomTextInput
                  label={t.period}
                  value={item.period}
                  disabled
                />
              </div>
              <div className="col-lg-4 col-md-9 col-sm-12 px-2 mb-3">
                <CustomDropdown
                  label={t.periodSubject}
                  value={
                    classSubjectList.find(
                      (opt) => String(opt.value) === String(item.subject),
                    ) || null
                  }
                  options={classSubjectList}
                  onChange={(v) => handleSubjectChange(index, v)}
                  placeholder={t.periodSubjectPlaceholder}
                  error={errors[`periodSubject_${index}`]}
                />
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* FOOTER ACTIONS */}
        <div className="d-flex justify-content-center gap-3 mt-4">
          <div className={styles.actionButtons}>
            <CustomButton
              variant="contained"
              onClick={handleSubmit}
              className={styles.saveButton}
              fullWidth={false}
              loading={loading}
              loadingText={t.saving}
            >
              {t.saveButton}
            </CustomButton>

            <CustomButton
              variant="contained"
              onClick={handleCancel}
              className={styles.cancelButton}
              fullWidth={false}
              disabled={loading}
            >
              {t.cancelButton}
            </CustomButton>

            {/* ✅ Delete button — only shown in edit mode */}
            {isEdit && (
              <CustomButton
                variant="contained"
                className={styles.deleteButton}
                onClick={() => setDeleteModal(true)}
                fullWidth={false}
                disabled={deleteLoading}
              >
                {deleteLoading ? t.deleting : t.delete}
              </CustomButton>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateTimeTable;
